import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Package, Search, ArrowUpDown, ArrowUp, ArrowDown, Upload, FileSpreadsheet, Loader2, Download, Settings, Tag, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Products = () => {
  const { isAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    price: '',
    stock: '',
    category: 'nutricion',
    image_url: ''
  });
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    slug: '',
    color: '#10B981'
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedProducts = useMemo(() => {
    let filtered = products.filter((product) => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.brand.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });

    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

    return filtered;
  }, [products, searchTerm, categoryFilter, sortField, sortDirection]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock)
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data);
        toast.success('Producto actualizado');
      } else {
        await axios.post(`${API}/products`, data);
        toast.success('Producto creado');
      }
      
      setShowModal(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar producto');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      brand: product.brand,
      price: product.price.toString(),
      stock: product.stock.toString(),
      category: product.category,
      image_url: product.image_url || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Producto eliminado');
      fetchProducts();
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      brand: '',
      price: '',
      stock: '',
      category: 'nutricion',
      image_url: ''
    });
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/products/import-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { products_created, products_updated, errors } = response.data;
      
      if (products_created > 0 || products_updated > 0) {
        toast.success(`Importación exitosa: ${products_created} creados, ${products_updated} actualizados`);
      }
      
      if (errors && errors.length > 0) {
        errors.forEach(err => toast.error(err));
      }
      
      fetchProducts();
      setShowImportModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar archivo');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadTemplate = () => {
    // Create a simple CSV template
    const headers = 'producto,marca,precio,stock';
    const example1 = 'Proteína Whey,Dymatize,899,10';
    const example2 = 'Creatina,ON,450,15';
    const content = `${headers}\n${example1}\n${example2}`;
    
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'plantilla_productos.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  // Category management functions
  const handleSaveCategory = async () => {
    if (!categoryFormData.name || !categoryFormData.slug) {
      toast.error('Nombre e identificador son requeridos');
      return;
    }
    
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, categoryFormData);
        toast.success('Categoría actualizada');
      } else {
        await axios.post(`${API}/categories`, categoryFormData);
        toast.success('Categoría creada');
      }
      setShowCategoryModal(false);
      setShowCategoryForm(false);
      setEditingCategory(null);
      setCategoryFormData({ name: '', slug: '', color: '#10B981' });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar categoría');
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`¿Eliminar la categoría "${category.name}"?`)) return;
    try {
      await axios.delete(`${API}/categories/${category.id}`);
      toast.success('Categoría eliminada');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar categoría');
    }
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({ name: category.name, slug: category.slug, color: category.color || '#10B981' });
    setShowCategoryForm(true);
  };

  const openNewCategory = () => {
    setEditingCategory(null);
    setShowCategoryForm(false);
    setCategoryFormData({ name: '', slug: '', color: '#10B981' });
    setShowCategoryModal(true);
  };

  // Helper to get category display name
  const getCategoryName = (slug) => {
    const cat = categories.find(c => c.slug === slug);
    return cat ? cat.name : slug;
  };

  const getCategoryColor = (slug) => {
    const cat = categories.find(c => c.slug === slug);
    return cat?.color || '#10B981';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(value);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={14} className="text-zinc-600" />;
    return sortDirection === 'asc' 
      ? <ArrowUp size={14} className="text-[#FF3B30]" />
      : <ArrowDown size={14} className="text-[#FF3B30]" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]" />
      </div>
    );
  }

  return (
    <div data-testid="products-page" className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-white">Productos</h1>
            <p className="text-zinc-500 text-sm">{products.length} productos</p>
          </div>
          <Button
            onClick={() => { fetchProducts(); fetchCategories(); }}
            variant="outline"
            size="sm"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
        {isAdmin() && (
          <div className="flex gap-2">
            <Button
              data-testid="import-products-btn"
              onClick={() => setShowImportModal(true)}
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-sm"
            >
              <Upload size={16} className="mr-2" />
              Importar Excel
            </Button>
            <Button
              data-testid="manage-categories-btn"
              onClick={openNewCategory}
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-sm"
            >
              <Tag size={16} className="mr-2" />
              Categorías
            </Button>
            <Button
              data-testid="add-product-btn"
              onClick={() => { resetForm(); setShowModal(true); }}
              className="bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-bold uppercase tracking-wider rounded-sm primary-glow text-sm"
            >
              <Plus size={16} className="mr-2" />
              Nuevo
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
          <Input
            data-testid="search-products"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar..."
            className="pl-10 bg-[#0A0A0A] border-zinc-800 focus:border-[#FF3B30] rounded-sm"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger 
            data-testid="category-filter"
            className="w-full sm:w-40 bg-[#0A0A0A] border-zinc-800"
          >
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-zinc-800">
            <SelectItem value="all">Todas</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Products Table */}
      <Card className="bg-[#0A0A0A] border-zinc-800">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead 
                    className="text-zinc-400 cursor-pointer hover:text-white transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-2">
                      Nombre
                      <SortIcon field="name" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-zinc-400 cursor-pointer hover:text-white transition-colors"
                    onClick={() => handleSort('brand')}
                  >
                    <div className="flex items-center gap-2">
                      Marca
                      <SortIcon field="brand" />
                    </div>
                  </TableHead>
                  <TableHead className="text-zinc-400 hidden sm:table-cell">Categoría</TableHead>
                  <TableHead className="text-zinc-400 text-right">Precio</TableHead>
                  <TableHead 
                    className="text-zinc-400 text-right cursor-pointer hover:text-white transition-colors"
                    onClick={() => handleSort('stock')}
                  >
                    <div className="flex items-center justify-end gap-2">
                      Stock
                      <SortIcon field="stock" />
                    </div>
                  </TableHead>
                  {isAdmin() && (
                    <TableHead className="text-zinc-400 text-right">Acciones</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedProducts.map((product) => (
                  <TableRow 
                    key={product.id} 
                    data-testid={`product-row-${product.id}`}
                    className="border-zinc-800 hover:bg-zinc-900/50"
                  >
                    <TableCell className="text-white font-medium">
                      {product.name}
                    </TableCell>
                    <TableCell className="text-zinc-400">
                      {product.brand}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <span 
                        className="px-2 py-1 rounded-sm text-xs"
                        style={{ 
                          backgroundColor: `${getCategoryColor(product.category)}20`,
                          color: getCategoryColor(product.category)
                        }}
                      >
                        {getCategoryName(product.category)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-[#FF3B30] font-bold">
                      {formatCurrency(product.price)}
                    </TableCell>
                    <TableCell className={`text-right font-mono ${
                      product.stock < 5 ? 'text-amber-500' : 'text-zinc-300'
                    }`}>
                      {product.stock}
                    </TableCell>
                    {isAdmin() && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            data-testid={`edit-product-${product.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(product)}
                            className="h-8 w-8 p-0 hover:bg-zinc-800"
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button
                            data-testid={`delete-product-${product.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(product.id)}
                            className="h-8 w-8 p-0 text-red-500 hover:bg-red-500/10"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {filteredAndSortedProducts.length === 0 && (
        <div className="text-center py-12">
          <Package size={48} className="mx-auto text-zinc-700 mb-4" />
          <p className="text-zinc-500">No se encontraron productos</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-zinc-400">Nombre</Label>
              <Input
                data-testid="product-name-input"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Marca</Label>
              <Input
                data-testid="product-brand-input"
                value={formData.brand}
                onChange={(e) => setFormData({...formData, brand: e.target.value})}
                required
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Precio</Label>
                <Input
                  data-testid="product-price-input"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  required
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Stock</Label>
                <Input
                  data-testid="product-stock-input"
                  type="number"
                  value={formData.stock}
                  onChange={(e) => setFormData({...formData, stock: e.target.value})}
                  required
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400">Categoría</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData({...formData, category: v})}
              >
                <SelectTrigger data-testid="product-category-select" className="bg-zinc-900 border-zinc-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-zinc-800">
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="border-zinc-700 w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid="save-product-btn"
                className="bg-[#FF3B30] hover:bg-[#D32F2F] w-full sm:w-auto"
              >
                {editingProduct ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Management Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Tag size={20} />
              {editingCategory ? 'Editar Categoría' : showCategoryForm ? 'Nueva Categoría' : 'Gestión de Categorías'}
            </DialogTitle>
          </DialogHeader>
          
          {/* List of existing categories */}
          {!editingCategory && !showCategoryForm && (
            <div className="space-y-2 mb-4">
              <p className="text-zinc-400 text-sm">Categorías existentes:</p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {categories.map(cat => (
                  <div key={cat.id} className="flex items-center justify-between bg-zinc-900 p-2 rounded-sm">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: cat.color }}
                      />
                      <span className="text-white">{cat.name}</span>
                      <span className="text-zinc-500 text-xs">({cat.slug})</span>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => openEditCategory(cat)}
                        className="h-7 w-7 p-0"
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeleteCategory(cat)}
                        className="h-7 w-7 p-0 text-red-400"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <Button 
                onClick={() => {
                  setShowCategoryForm(true);
                  setCategoryFormData({ name: '', slug: '', color: '#10B981' });
                }}
                variant="outline"
                className="w-full border-zinc-700 mt-2"
              >
                <Plus size={16} className="mr-2" />
                Nueva Categoría
              </Button>
            </div>
          )}
          
          {/* Form for new/edit category */}
          {(editingCategory || showCategoryForm) && (
            <div className="space-y-4 border-t border-zinc-800 pt-4">
              <div>
                <Label className="text-zinc-400">Nombre</Label>
                <Input
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({
                    ...categoryFormData, 
                    name: e.target.value,
                    slug: editingCategory ? categoryFormData.slug : e.target.value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '_')
                  })}
                  placeholder="Ej: Suplementos"
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Identificador (slug)</Label>
                <Input
                  value={categoryFormData.slug}
                  onChange={(e) => setCategoryFormData({...categoryFormData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')})}
                  placeholder="Ej: suplementos"
                  className="bg-zinc-900 border-zinc-800"
                />
                <p className="text-zinc-500 text-xs mt-1">Solo letras minúsculas, números y guiones bajos</p>
              </div>
              <div>
                <Label className="text-zinc-400">Color</Label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={categoryFormData.color}
                    onChange={(e) => setCategoryFormData({...categoryFormData, color: e.target.value})}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={categoryFormData.color}
                    onChange={(e) => setCategoryFormData({...categoryFormData, color: e.target.value})}
                    placeholder="#10B981"
                    className="bg-zinc-900 border-zinc-800 flex-1"
                  />
                </div>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (editingCategory || showCategoryForm) {
                      setEditingCategory(null);
                      setShowCategoryForm(false);
                      setCategoryFormData({ name: '', slug: '', color: '#10B981' });
                    } else {
                      setShowCategoryModal(false);
                    }
                  }}
                  className="border-zinc-700 w-full sm:w-auto"
                >
                  {(editingCategory || showCategoryForm) ? 'Volver' : 'Cancelar'}
                </Button>
                <Button
                  onClick={handleSaveCategory}
                  className="bg-[#FF3B30] hover:bg-[#D32F2F] w-full sm:w-auto"
                >
                  {editingCategory ? 'Guardar' : 'Crear'}
                </Button>
              </DialogFooter>
            </div>
          )}
          
          {!editingCategory && !showCategoryForm && (
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowCategoryModal(false)}
                className="border-zinc-700"
              >
                Cerrar
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Excel Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileSpreadsheet size={20} className="text-emerald-500" />
              Importar Productos desde Excel
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-zinc-900 p-4 rounded-sm border border-zinc-800">
              <h4 className="text-white font-medium mb-2">Formato requerido</h4>
              <p className="text-zinc-400 text-sm mb-3">
                El archivo Excel debe tener las siguientes columnas:
              </p>
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div className="bg-zinc-800 p-2 rounded text-center">
                  <span className="text-emerald-400 font-mono">producto</span>
                </div>
                <div className="bg-zinc-800 p-2 rounded text-center">
                  <span className="text-emerald-400 font-mono">marca</span>
                </div>
                <div className="bg-zinc-800 p-2 rounded text-center">
                  <span className="text-emerald-400 font-mono">precio</span>
                </div>
                <div className="bg-zinc-800 p-2 rounded text-center">
                  <span className="text-zinc-500 font-mono">stock*</span>
                </div>
              </div>
              <p className="text-zinc-500 text-xs mt-2">*Stock es opcional (por defecto: 10)</p>
            </div>

            <Button
              variant="outline"
              onClick={downloadTemplate}
              className="w-full border-zinc-700 text-zinc-300"
            >
              <Download size={16} className="mr-2" />
              Descargar plantilla CSV
            </Button>

            <div className="border-2 border-dashed border-zinc-700 rounded-sm p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleImportExcel}
                className="hidden"
                id="excel-upload"
              />
              <label
                htmlFor="excel-upload"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                {importing ? (
                  <>
                    <Loader2 size={32} className="text-[#FF3B30] animate-spin" />
                    <span className="text-zinc-400">Importando...</span>
                  </>
                ) : (
                  <>
                    <Upload size={32} className="text-zinc-500" />
                    <span className="text-zinc-400">Click para seleccionar archivo</span>
                    <span className="text-zinc-600 text-xs">.xlsx, .xls o .csv</span>
                  </>
                )}
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowImportModal(false)}
              className="border-zinc-700"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;
